/**
 * Client-tag scoping for the acting (logged-in) user.
 *
 * Req #4a: a Client Admin (UserRoleName.OrganizationAdmin) may only assign
 * their OWN client tag(s) to users they create/edit. With a single tag the
 * created user is forced to that tag; with multiple they pick among their own.
 * Care Advisors and Collectors get the same treatment — they must only see
 * and select client tags they're themselves assigned to, exactly like the
 * division scoping in `clientDivisionActorScope.ts`. Admin / SuperAdmin (and
 * every other actor) keep the full option list — the backend remains the
 * source of truth and enforces the same policy.
 */
import { UserRoleName } from './types/UserProfile';

interface ActingUserLike {
  role?: { name?: string } | string | null;
  clients?: Array<{ id: number | string }> | null;
}

const SCOPED_ROLES: ReadonlySet<string> = new Set([
  UserRoleName.OrganizationAdmin,
  UserRoleName.CareAdvisor,
  UserRoleName.Collector,
]);

function actingRoleName(user: ActingUserLike | null | undefined): string | undefined {
  if (!user) return undefined;
  return typeof user.role === 'string' ? user.role : user.role?.name;
}

function actingOwnedClientIds(user: ActingUserLike | null | undefined): string[] {
  return (user?.clients || [])
    .map((c) => (c == null ? '' : String(c.id)))
    .filter((id) => id !== '' && id !== 'undefined' && id !== 'null');
}

/**
 * Restrict client-tag options to the actor's own tags. No-op for any other
 * acting role (Admin/SuperAdmin see all tags).
 *
 * A CareAdvisor/Collector with no client tags of their own is "unscoped" —
 * a legitimate, pre-existing state elsewhere in this codebase (see the
 * `!user.clients || user.clients.length === 0` checks in `UsersPage.tsx` /
 * `PatientSelect.tsx`) — and keeps seeing the full list, exactly like
 * `scopeDivisionOptionsToActor` falls through when the actor owns no
 * divisions. An Org Admin is never let off this way: Req #4a locks them to
 * their own tag set even when that set is empty.
 *
 * `preserveIds` keeps a user's already-assigned tag visible even if it isn't
 * one of the actor's own — an actor editing someone else's profile can read
 * from an unscoped tag list (e.g. an Org Admin from `/admin/clients`), so
 * without this the existing tag would silently vanish from the picker
 * instead of just being unchangeable to something else out of scope.
 */
export function scopeClientTagOptionsToActor<T extends { id?: unknown; value?: unknown }>(
  options: T[],
  actingUser: ActingUserLike | null | undefined,
  preserveIds: Array<string | number> = [],
): T[] {
  const role = actingRoleName(actingUser);
  if (!role || !SCOPED_ROLES.has(role)) {
    return options;
  }
  const ownedIds = new Set(actingOwnedClientIds(actingUser));
  if (role !== UserRoleName.OrganizationAdmin && ownedIds.size === 0) {
    return options;
  }
  preserveIds.forEach((id) => ownedIds.add(String(id)));
  return options.filter((option) => ownedIds.has(String(option.id ?? option.value)));
}

/**
 * When a Client Admin owns exactly one client tag, users they create/edit are
 * forced to that tag (Req #4a.1). Returns the tag id as a string, else null.
 */
export function forcedActorClientTagId(actingUser: ActingUserLike | null | undefined): string | null {
  if (actingRoleName(actingUser) !== UserRoleName.OrganizationAdmin) {
    return null;
  }
  const ownedIds = actingOwnedClientIds(actingUser);
  return ownedIds.length === 1 ? ownedIds[0] : null;
}
